import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import { confirmAsync } from '../utils/notify';
import './TikTokConnection.css';

/**
 * TikTok Connection component — tabbed layout.
 *
 * Tab 1 "Publishing":  Login Kit OAuth (open.tiktokapis.com)
 *   → For video publishing via Content Posting API v2
 *
 * Tab 2 "Business":    Business API OAuth (business-api.tiktok.com/portal/auth)
 *   → For comment management, DMs, insights
 *
 * Both flows share a single TikTokAccount DB row per user.
 */
function TikTokConnection() {
  const { t } = useTranslation();
  const popupRef = useRef(null);
  const popupCheckIntervalRef = useRef(null);

  const [activeTab, setActiveTab] = useState('publishing');
  const [connectedAccount, setConnectedAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch connected account status
  const fetchAccountStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/v1/social/tiktok/account');
      setConnectedAccount(response.data);
    } catch (err) {
      if (err.response?.status !== 401 && err.response?.status !== 404) {
        setError(t('tiktok.errorFetching'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAccountStatus();
  }, [fetchAccountStatus]);

  // Listen for BOTH OAuth callback messages from popup
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;

      const data = event.data;

      // Login Kit callback
      if (data?.type === 'TIKTOK_OAUTH_CALLBACK') {
        clearPopupCheck();
        if (data.success) {
          setSuccess(t('tiktok.connectSuccess', { name: data.display_name || 'TikTok' }));
          fetchAccountStatus();
        } else if (data.error) {
          setError(data.error);
        }
        setConnecting(false);
      }

      // Business API callback
      if (data?.type === 'TIKTOK_BUSINESS_OAUTH_CALLBACK') {
        clearPopupCheck();
        if (data.success) {
          setSuccess(t('tiktok.connectSuccess', { name: data.business_id || 'TikTok Business' }));
          fetchAccountStatus();
        } else if (data.error) {
          setError(data.error);
        }
        setConnecting(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [t, fetchAccountStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearPopupCheck();
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  const clearPopupCheck = () => {
    if (popupCheckIntervalRef.current) {
      clearInterval(popupCheckIntervalRef.current);
      popupCheckIntervalRef.current = null;
    }
  };

  const openOAuthPopup = (oauthUrl, windowName) => {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }

    popupRef.current = window.open(
      oauthUrl,
      windowName,
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    if (!popupRef.current) {
      setError(t('tiktok.errors.popupBlocked'));
      setConnecting(false);
      return;
    }

    clearPopupCheck();
    popupCheckIntervalRef.current = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        clearPopupCheck();
        setConnecting((current) => {
          if (current) {
            setError(t('tiktok.errors.loginCancelled'));
            return false;
          }
          return current;
        });
      }
    }, 500);
  };

  // Start Login Kit OAuth (for video publishing)
  const handleConnectLoginKit = async () => {
    setConnecting(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await apiClient.get('/api/v1/social/tiktok/oauth/start');
      openOAuthPopup(response.data.oauth_url, 'tiktok_login_kit_oauth');
    } catch (err) {
      console.error('Failed to start TikTok Login Kit OAuth:', err);
      setError(t('tiktok.errors.connectionFailed'));
      setConnecting(false);
    }
  };

  // Start Business API OAuth (for comments/DM)
  const handleConnectBusiness = async () => {
    setConnecting(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await apiClient.get('/api/v1/social/tiktok/business/oauth/start');
      openOAuthPopup(response.data.oauth_url, 'tiktok_business_oauth');
    } catch (err) {
      console.error('Failed to start TikTok Business OAuth:', err);
      setError(t('tiktok.errors.connectionFailed'));
      setConnecting(false);
    }
  };

  // Disconnect specific connection type
  const handleDisconnect = async (connectionType) => {
    const confirmMsg = connectionType === 'all'
      ? t('tiktok.confirmDisconnect')
      : t('tiktok.confirmDisconnectPartial', { type: connectionType });
    if (!(await confirmAsync({ message: confirmMsg, danger: true }))) return;

    setDisconnecting(true);
    setError(null);
    try {
      await apiClient.delete(`/api/v1/social/tiktok/disconnect?connection_type=${connectionType}`);
      if (connectionType === 'all') {
        setConnectedAccount(null);
      } else {
        await fetchAccountStatus();
      }
      setSuccess(t('tiktok.disconnectSuccess'));
    } catch (err) {
      const message = err.response?.data?.detail || t('tiktok.errorDisconnecting');
      setError(message);
    } finally {
      setDisconnecting(false);
    }
  };

  // Refresh token and profile info
  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const response = await apiClient.post('/api/v1/social/tiktok/refresh');
      if (response.data.account) {
        setConnectedAccount((prev) => ({
          ...prev,
          ...response.data.account,
        }));
      }
      setSuccess(t('tiktok.refreshSuccess'));
    } catch (err) {
      const message = err.response?.data?.detail || t('tiktok.errorRefreshing');
      setError(message);
    } finally {
      setRefreshing(false);
    }
  };

  // Clear messages after timeout
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (loading) {
    return (
      <div className="tiktok-connection loading">
        <div className="spinner" />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  const hasLoginKit = connectedAccount?.has_login_kit_token;
  const loginKitValid = connectedAccount?.login_kit_token_valid;
  const hasBusiness = connectedAccount?.has_business_token;
  const businessValid = connectedAccount?.business_token_valid;

  return (
    <div className="tiktok-connection">
      {/* Header */}
      <div className="section-header">
        <div className="header-icon">🎵</div>
        <div className="header-text">
          <h2>{t('tiktok.connectionTitle')}</h2>
          <p className="description">{t('tiktok.connectionDescription')}</p>
        </div>
      </div>

      {/* Success / Error Messages */}
      {success && (
        <div className="alert alert-success">
          <span className="icon">✓</span>
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="dismiss">×</button>
        </div>
      )}
      {error && (
        <div className="alert alert-error">
          <span className="icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="dismiss">×</button>
        </div>
      )}

      {/* Profile card (shared between tabs) */}
      {connectedAccount && (
        <div className="account-card">
          <div className="account-info">
            {connectedAccount.avatar_url ? (
              <img
                src={connectedAccount.avatar_url}
                alt={connectedAccount.display_name}
                className="profile-picture"
              />
            ) : (
              <div className="profile-picture placeholder">
                <span>🎵</span>
              </div>
            )}
            <div className="account-details">
              <h3>{connectedAccount.display_name || 'TikTok User'}</h3>
              <div className="stats">
                {connectedAccount.follower_count != null && (
                  <span className="stat">
                    <strong>{connectedAccount.follower_count.toLocaleString()}</strong> {t('tiktok.followers')}
                  </span>
                )}
                {connectedAccount.video_count != null && (
                  <span className="stat">
                    <strong>{connectedAccount.video_count.toLocaleString()}</strong> {t('tiktok.videos')}
                  </span>
                )}
                {connectedAccount.likes_count != null && (
                  <span className="stat">
                    <strong>{connectedAccount.likes_count.toLocaleString()}</strong> {t('tiktok.likes')}
                  </span>
                )}
              </div>
              <p className="connected-date">
                {t('tiktok.connectedOn', {
                  date: new Date(connectedAccount.connected_at).toLocaleDateString(),
                })}
              </p>
            </div>
            {/* Combined status badges */}
            <div className="connection-badges">
              <div className={`mini-badge ${hasLoginKit && loginKitValid ? 'active' : hasLoginKit ? 'expired' : 'none'}`}>
                📹 {t('tiktok.tabs.publishing')}
              </div>
              {/* TODO: temporarily disabled — re-enable when Business API is ready
              <div className={`mini-badge ${hasBusiness && businessValid ? 'active' : hasBusiness ? 'expired' : 'none'}`}>
                💼 {t('tiktok.tabs.business')}
              </div>
              */}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tiktok-tabs">
        <button
          className={`tiktok-tab ${activeTab === 'publishing' ? 'active' : ''}`}
          onClick={() => setActiveTab('publishing')}
        >
          📹 {t('tiktok.tabs.publishing')}
        </button>
        {/* TODO: temporarily disabled — re-enable when Business API is ready
        <button
          className={`tiktok-tab ${activeTab === 'business' ? 'active' : ''}`}
          onClick={() => setActiveTab('business')}
        >
          💼 {t('tiktok.tabs.business')}
        </button>
        */}
      </div>

      {/* Tab Content */}
      <div className="tiktok-tab-content">
        {activeTab === 'publishing' && (
          <div className="tab-panel">
            <p className="tab-description">{t('tiktok.publishingDescription')}</p>

            {hasLoginKit ? (
              <div className="connection-status">
                <div className={`status-indicator ${loginKitValid ? 'connected' : 'expired'}`}>
                  <span className="status-dot" />
                  <span>{loginKitValid ? t('tiktok.loginKitConnected') : t('tiktok.loginKitTokenExpired')}</span>
                </div>
                {connectedAccount.login_kit_token_expires_at && (
                  <p className="token-expiry">
                    {t('tiktok.tokenExpiresAt', {
                      date: new Date(connectedAccount.login_kit_token_expires_at).toLocaleString(),
                    })}
                  </p>
                )}
                <div className="tab-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={handleRefresh}
                    disabled={refreshing}
                  >
                    {refreshing ? '...' : '🔄'} {t('tiktok.refresh')}
                  </button>
                  <button
                    className="btn btn-outline"
                    onClick={handleConnectLoginKit}
                    disabled={connecting}
                  >
                    {connecting ? t('tiktok.connecting') : t('tiktok.reconnectLoginKit')}
                  </button>
                  <button
                    className="btn btn-danger-outline"
                    onClick={() => handleDisconnect('login_kit')}
                    disabled={disconnecting}
                  >
                    {disconnecting ? t('common.processing') : t('tiktok.disconnectLoginKit')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="connect-section">
                <div className="connect-prompt-small">
                  <p>{t('tiktok.loginKitNotConnected')}</p>
                  <ul className="requirements">
                    <li>{t('tiktok.publishReq1')}</li>
                    <li>{t('tiktok.publishReq2')}</li>
                  </ul>
                </div>
                <button
                  className="btn btn-primary tiktok-connect-btn"
                  onClick={handleConnectLoginKit}
                  disabled={connecting}
                >
                  {connecting ? (
                    <><span className="spinner-small" /> {t('tiktok.connecting')}</>
                  ) : (
                    <>📹 {t('tiktok.connectLoginKit')}</>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* TODO: temporarily disabled — re-enable when Business API is ready
        {activeTab === 'business' && (
          <div className="tab-panel">
            <p className="tab-description">{t('tiktok.businessDescription')}</p>

            {hasBusiness ? (
              <div className="connection-status">
                <div className={`status-indicator ${businessValid ? 'connected' : 'expired'}`}>
                  <span className="status-dot" />
                  <span>{businessValid ? t('tiktok.businessConnected') : t('tiktok.businessTokenExpired')}</span>
                </div>
                {connectedAccount.business_token_type && (
                  <p className="token-type">
                    {t('tiktok.businessTokenTypeLabel')}: <strong>{connectedAccount.business_token_type}</strong>
                  </p>
                )}
                <div className="tab-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={handleRefresh}
                    disabled={refreshing}
                  >
                    {refreshing ? '...' : '🔄'} {t('tiktok.refresh')}
                  </button>
                  <button
                    className="btn btn-outline"
                    onClick={handleConnectBusiness}
                    disabled={connecting}
                  >
                    {connecting ? t('tiktok.connecting') : t('tiktok.reconnectBusiness')}
                  </button>
                  <button
                    className="btn btn-danger-outline"
                    onClick={() => handleDisconnect('business')}
                    disabled={disconnecting}
                  >
                    {disconnecting ? t('common.processing') : t('tiktok.disconnectBusiness')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="connect-section">
                <div className="connect-prompt-small">
                  <p>{t('tiktok.businessNotConnected')}</p>
                  <ul className="requirements">
                    <li>{t('tiktok.requirement1')}</li>
                    <li>{t('tiktok.requirement2')}</li>
                  </ul>
                </div>
                <button
                  className="btn btn-primary tiktok-connect-btn"
                  onClick={handleConnectBusiness}
                  disabled={connecting}
                >
                  {connecting ? (
                    <><span className="spinner-small" /> {t('tiktok.connecting')}</>
                  ) : (
                    <>💼 {t('tiktok.connectBusiness')}</>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
        */}
      </div>

      {/* Global disconnect (both connections) — only show if at least one connected */}
      {connectedAccount && (hasLoginKit || hasBusiness) && (
        <div className="global-actions">
          <button
            className="btn btn-danger btn-small"
            onClick={() => handleDisconnect('all')}
            disabled={disconnecting}
          >
            {disconnecting ? t('common.processing') : t('tiktok.disconnectAll')}
          </button>
        </div>
      )}
    </div>
  );
}

export default TikTokConnection;
